<template>
    <div
        class="relative inline-block w-full"
        v-click-outside="() => (showCardsList = false)"
    >
        <div class="flex items-start gap-2 relative pr-4" v-if="currentCard">
            <img
                :src="
                    cdnUrl +
                    '/' +
                    currentCard.card_image.path +
                    '/_small/' +
                    currentCard.card_image.file_name
                "
                class="w-6 h-auto object-cover rounded mt-1"
            />
            <span class="text-sm">{{ currentCard.title }}</span>
            <a
                href="#"
                class="absolute -top-1 right-1 text-orange-700 hover:text-orange-500 transition-colors outline-none font-medium text-lg"
                @click.prevent="clearCurrentCard()"
            >
                <i class="fal fa-times"></i>
            </a>
        </div>
        <Input
            :label="label"
            :placeholder="placeholder"
            id="card-search"
            name="card_search"
            :errors="{ card_search: validationError }"
            :options="{
                icon: 'search',
                iconPosition: 'left',
            }"
            :disabled="disabled"
            v-if="!currentCard"
        >
            <template #label>
                <span class="block w-full mb-2 text-sm leading-tight">
                    {{ label }}
                </span>
            </template>
            <template #input>
                <input
                    type="text"
                    class="block w-full 3 py-2 text-gray-900 placeholder:text-slate-500 placeholder:text-sm transition-colors focus:ring-0 bg-white rounded border border-slate-300 focus:border-slate-800 hover:border-slate-800 disabled:opacity-50 disabled:cursor-not-allowed :disabled:!bg-slate-300 pl-10"
                    @keydown.enter.prevent="enter"
                    @keydown.down="down"
                    @keydown.up="up"
                    @input="change"
                    name="card_search"
                    v-show="queryInputActive"
                    v-model="query"
                    ref="searchinput"
                    :required="required"
                    :placeholder="placeholder"
                    :class="{
                        '!border-red-500': validationError,
                    }"
                    :disabled="disabled"
                    v-debounce="200"
                    autocomplete="off"
                />
                <input
                    type="text"
                    class="block w-full bg-white rounded border border-slate-200 py-1.5 text-gray-900 placeholder:text-slate-500 placeholder:text-sm transition-colors focus:ring-0 focus:border-slate-800 hover:border-slate-800 disabled:opacity-50 disabled:cursor-not-allowed pl-10"
                    name="card_search"
                    v-model="itemName"
                    v-show="!queryInputActive"
                    @click="activateSearch()"
                    :required="required"
                    placeholder="Search for card"
                    :class="{
                        '!border-red-500': validationError,
                    }"
                />
            </template>
        </Input>

        <Teleport to="body">
            <transition name="fade">
                <div
                    class="max-h-48 min-w-[380px] p-2 overflow-y-auto bg-white rounded-lg shadow-sm border border-slate-200 absolute z-[99999]"
                    :style="`width: ${width}px; top: ${top}px; left: ${left}px`"
                    v-show="showCardsList"
                >
                    <ul class="w-full divide-y divide-slate-200">
                        <li
                            v-for="(item, index) in results"
                            :key="'item-id' + index"
                            class="px-4 py-2 hover:bg-slate-50 cursor-pointer"
                            :class="{ 'bg-slate-50': item.active }"
                            @click="chooseItem(item)"
                        >
                            <p class="text-slate-500 text-sm mb-1 pt-1">
                                {{ item.issuing_bank?.name }}
                            </p>
                            <p class="flex items-center gap-2">
                                <ImageLoader>
                                    <template #image>
                                        <img
                                            :src="
                                                cdnUrl +
                                                '/' +
                                                item.card_image.path +
                                                '/_small/' +
                                                item.card_image.file_name
                                            "
                                            :alt="item.title"
                                            class="w-8 h-auto rounded-sm"
                                            v-if="item.card_image"
                                        />
                                    </template>
                                    <template #error>
                                        <Icon
                                            icon="credit-card"
                                            class="text-slate-600 w-8 h-5"
                                        />
                                    </template>
                                </ImageLoader>
                                <span class="text-sm">{{ item.title }}</span>
                            </p>
                        </li>
                    </ul>
                </div>
            </transition>
        </Teleport>
    </div>
</template>

<script setup>
import { ref, watch, onMounted, onBeforeUnmount } from 'vue';
import Input from '@/components/ui/inputs/ui-input.vue';
import ImageLoader from '@/components/ui/image-loader/image-loader.vue';
import Icon from '@/components/ui/icon/icon.vue';

const props = defineProps({
    required: {
        type: Boolean,
        default: false,
    },
    disabled: {
        type: Boolean,
        default: false,
    },
    validationError: {
        type: String,
        default: '',
    },
    institutionCode: {
        type: String,
        default: '',
    },
    label: {
        type: [String, Boolean],
        default: 'Search for card',
    },
    placeholder: {
        type: String,
        default: 'Search for card',
    },
    currentCard: {
        type: [Object, null],
        default: null,
    },
});

const emit = defineEmits(['card-selected', 'clear-current-card']);
const modelValue = defineModel('modelValue');

const query = ref('');
const itemId = ref('');
const itemName = ref('');
const results = ref([]);
const selected = ref(-1);
const width = ref(0);
const top = ref(0);
const left = ref(0);
const queryInputActive = ref(true);
const searchinput = ref(null);
const cdnUrl = window.cdnUrl;
const showCardsList = ref(false);
const chosenCard = ref(null);

defineOptions({
    directives: {
        'click-outside': {
            mounted(el, binding) {
                el.clickOutsideEvent = (event) => {
                    if (!(el === event.target || el.contains(event.target))) {
                        binding.value(event);
                    }
                };
                document.addEventListener('click', el.clickOutsideEvent);
            },
            unmounted(el) {
                document.removeEventListener('click', el.clickOutsideEvent);
            },
        },
    },
});

watch(showCardsList, (val) => {
    if (val) {
        setCardsDropdownPosition();
    }
});

function change(e) {
    if (e.target.value.length >= 2) {
        query.value = e.target.value;
        getItems();
    } else {
        results.value = [];
        showCardsList.value = false;
    }
}

function clearCurrentCard() {
    emit('clear-current-card');
}

async function getItems() {
    try {
        const response = await window.ApiService.apiCall(
            `/cards/user-search?s=${query.value}&institution_code=${props.institutionCode}`,
            'get'
        );
        results.value = response.data.data;
        if (results.value.length > 0) {
            results.value[0].active = true;
            selected.value = 0;
            showCardsList.value = true;
        }
    } catch (error) {
        console.error('Error fetching cards:', error);
        results.value = [];
        showCardsList.value = false;
    }
}

function chooseItem(item) {
    itemId.value = item.id;
    itemName.value = item.title;
    queryInputActive.value = false;
    results.value = [];
    showCardsList.value = false;
    chosenCard.value = item;
    emit('card-selected', item);
}

function up() {
    if (selected.value >= 0) {
        if (selected.value > 0) {
            selected.value--;
        }
        resetActiveItem();
        results.value[selected.value].active = true;
    }
}

function down() {
    if (selected.value + 1 < results.value.length) {
        selected.value++;
        resetActiveItem();
        results.value[selected.value].active = true;
    }
}

function resetActiveItem() {
    results.value.forEach((item) => {
        item.active = false;
    });
}

function enter() {
    if (selected.value >= 0 && results.value.length > 0) {
        const item = results.value[selected.value];
        chooseItem(item);
    }
}

function activateSearch() {
    queryInputActive.value = true;
    itemId.value = '';
    itemName.value = '';
    query.value = '';
    setTimeout(() => {
        searchinput.value.focus();
    }, 200);
}

const setCardsDropdownPosition = () => {
    if (!searchinput.value) return;
    const rect = searchinput.value.getBoundingClientRect();
    width.value = rect.width;
    top.value = rect.bottom + window.scrollY + 4;
    left.value = rect.left + window.scrollX;
};

// Expose clear method to parent
defineExpose({
    clear() {
        query.value = '';
        itemId.value = '';
        itemName.value = '';
        results.value = [];
        selected.value = -1;
        queryInputActive.value = true;
    },
});

onMounted(() => {
    setCardsDropdownPosition();
    window.addEventListener('resize', setCardsDropdownPosition);
    window.addEventListener('scroll', setCardsDropdownPosition);
});

onBeforeUnmount(() => {
    window.removeEventListener('resize', setCardsDropdownPosition);
    window.removeEventListener('scroll', setCardsDropdownPosition);
});
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}
</style>
